/**
 * Timezone Utilities
 * Handle timezone conversions with user-configurable timezones
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * Default timezone fallback when user has no preference
 */
export const DEFAULT_TIMEZONE = 'UTC';

/**
 * Parse ISO datetime string in specified timezone
 *
 * @param isoString - ISO 8601 datetime string (e.g., "2025-10-21T08:00:00Z" or "2025-10-21T08:00:00")
 * @param tz - IANA timezone string (e.g., "Europe/Belgrade", "America/New_York")
 * @returns dayjs instance in specified timezone
 */
export const parseInTimezone = (
  isoString: string,
  tz: string = DEFAULT_TIMEZONE
) => {
  // Parse as UTC first, then convert to target timezone
  // This ensures consistent handling of datetime strings regardless of Z suffix
  return dayjs.utc(isoString).tz(tz);
};

/**
 * @deprecated Use parseInTimezone with explicit timezone parameter
 * Parse ISO datetime string in Belgrade timezone
 */
export const parseInBelgradeTimezone = (isoString: string) => {
  return parseInTimezone(isoString, 'Europe/Belgrade');
};

/**
 * Get hours in specified timezone
 *
 * @param isoString - ISO 8601 datetime string
 * @param tz - IANA timezone string
 * @returns hour (0-23) in specified timezone
 */
export const getHours = (
  isoString: string,
  tz: string = DEFAULT_TIMEZONE
): number => {
  return parseInTimezone(isoString, tz).hour();
};

/**
 * Get minutes in specified timezone
 *
 * @param isoString - ISO 8601 datetime string
 * @param tz - IANA timezone string
 * @returns minutes (0-59) in specified timezone
 */
export const getMinutes = (
  isoString: string,
  tz: string = DEFAULT_TIMEZONE
): number => {
  return parseInTimezone(isoString, tz).minute();
};

/**
 * Format time in specified timezone
 *
 * @param isoString - ISO 8601 datetime string
 * @param format - dayjs format string (default: 'HH:mm')
 * @param tz - IANA timezone string
 * @returns formatted time string in specified timezone
 */
export const formatTime = (
  isoString: string,
  format: string = 'HH:mm',
  tz: string = DEFAULT_TIMEZONE
): string => {
  return parseInTimezone(isoString, tz).format(format);
};

/**
 * Format date in specified timezone
 *
 * @param isoString - ISO 8601 datetime string
 * @param format - dayjs format string (default: 'YYYY-MM-DD')
 * @param tz - IANA timezone string
 * @returns formatted date string in specified timezone
 */
export const formatDate = (
  isoString: string,
  format: string = 'YYYY-MM-DD',
  tz: string = DEFAULT_TIMEZONE
): string => {
  return parseInTimezone(isoString, tz).format(format);
};

/**
 * Format datetime in specified timezone
 *
 * @param isoString - ISO 8601 datetime string
 * @param format - dayjs format string (default: 'YYYY-MM-DD HH:mm')
 * @param tz - IANA timezone string
 * @returns formatted datetime string in specified timezone
 */
export const formatDateTime = (
  isoString: string,
  format: string = 'YYYY-MM-DD HH:mm',
  tz: string = DEFAULT_TIMEZONE
): string => {
  return parseInTimezone(isoString, tz).format(format);
};

/**
 * Convert local datetime to UTC ISO string for API submission
 *
 * @param localDate - Date object in user's local timezone
 * @param tz - IANA timezone string
 * @returns UTC ISO string (e.g., "2024-01-15T10:30:00.000Z")
 */
export const toUTC = (
  localDate: Date,
  tz: string = DEFAULT_TIMEZONE
): string => {
  return dayjs(localDate).tz(tz, true).utc().toISOString();
};

/**
 * Convert dayjs object to ISO string WITH timezone offset
 *
 * Used for API submissions where backend needs timezone information.
 * Backend will handle timezone conversion and store in UTC.
 *
 * @param dayjsObj - dayjs instance in user's timezone
 * @returns ISO string with timezone offset (e.g., "2025-11-18T12:00:00-06:00" for CST, "2025-11-18T12:00:00+01:00" for CET)
 *
 * @example
 * ```typescript
 * const userTime = dayjs.tz('2025-11-18 12:00', 'America/Chicago');
 * const iso = toISOWithTimezone(userTime);
 * // Returns: "2025-11-18T12:00:00-06:00" (CST)
 * ```
 */
export const toISOWithTimezone = (dayjsObj: dayjs.Dayjs): string => {
  return dayjsObj.format();
};

/**
 * Format relative time (e.g., "2 hours ago")
 *
 * @param isoString - ISO 8601 datetime string
 * @param tz - IANA timezone string
 * @returns relative time string
 */
export const formatRelative = (
  isoString: string,
  tz: string = DEFAULT_TIMEZONE
): string => {
  return parseInTimezone(isoString, tz).fromNow();
};

/**
 * Get current time in specified timezone
 *
 * @param tz - IANA timezone string
 * @returns dayjs instance of current time in specified timezone
 */
export const getCurrentTime = (tz: string = DEFAULT_TIMEZONE): dayjs.Dayjs => {
  return dayjs().tz(tz);
};

// ============================================
// DEPRECATED FUNCTIONS (for backwards compatibility)
// Use the new generic functions with explicit timezone parameter
// ============================================

/**
 * @deprecated Use getHours with explicit timezone parameter
 */
export const getHoursInBelgrade = (isoString: string): number => {
  return getHours(isoString, 'Europe/Belgrade');
};

/**
 * @deprecated Use getMinutes with explicit timezone parameter
 */
export const getMinutesInBelgrade = (isoString: string): number => {
  return getMinutes(isoString, 'Europe/Belgrade');
};

/**
 * @deprecated Use formatTime with explicit timezone parameter
 */
export const formatTimeInBelgrade = (
  isoString: string,
  format: string = 'HH:mm'
): string => {
  return formatTime(isoString, format, 'Europe/Belgrade');
};

/**
 * @deprecated Use formatDate with explicit timezone parameter
 */
export const formatDateInBelgrade = (isoString: string): string => {
  return formatDate(isoString, 'YYYY-MM-DD', 'Europe/Belgrade');
};
